import * as actions from './actionTypes';
import { getFirebaseErrorMessage } from '../../Utils/firebaseErrors';
import { deleteMyAccount } from '../../Services/accountLifecycle';
import { makeGeoLocation } from '../../Utils/geo';
import { normalizeRegionCode, normalizeRegionList } from '../../Config/franceRegions';
import { sendPasswordReset, sendVerificationEmail } from '../../Utils/authEmailActions';

const normalizeText = (value) => (typeof value === 'string' ? value.trim() : value);

const createAccount = (role) => (data) => async (
  dispatch,
  getState,
  { getFirebase, getFirestore },
) => {
  const firebase = getFirebase();
  const firestore = getFirestore();
  let createdUser = null;

  dispatch({ type: actions.AUTH_START });

  try {
    if (!data.acceptedTerms || !data.termsVersion) {
      throw new Error('Vous devez accepter les conditions d’utilisation avant de créer un compte.');
    }

    const firstName = normalizeText(data.firstName);
    const lastName = normalizeText(data.lastName);
    const email = normalizeText(data.email).toLowerCase();

    const credential = await firebase
      .auth()
      .createUserWithEmailAndPassword(email, data.password);
    createdUser = credential.user;

    // Firebase email templates use %DISPLAY_NAME%. Without this update the
    // greeting in the verification message is empty.
    await createdUser.updateProfile({
      displayName: `${firstName} ${lastName}`.trim(),
    });

    const baseProfile = {
      firstName,
      lastName,
      email,
      user_id: createdUser.uid,
      user_type: role,
      phone_number: '',
      address: '',
      profile_image: '',
      legal_acceptance: {
        accepted: Boolean(data.acceptedTerms),
        terms_version: data.termsVersion || '',
        review_policy_version: data.reviewPolicyVersion || '',
        accepted_at: firebase.firestore.FieldValue.serverTimestamp(),
      },
      created_at: firebase.firestore.FieldValue.serverTimestamp(),
      updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    };

    const roleProfile = role === 'Artisan'
      ? {
          speciality: '',
          registration_status: 'pending',
          artisan_region: '',
          service_regions: [],
          accepts_outside_regions: false,
          nationwide_service: false,
          artisan_rating: 0,
          total_num_jobs: 0,
        }
      : {};

    await firestore
      .collection('users')
      .doc(createdUser.uid)
      .set({ ...baseProfile, ...roleProfile }, { merge: true });

    // Email delivery is intentionally isolated from account creation. A
    // temporary DNS, template or Authorized domains problem must not delete a
    // valid Firebase Authentication account and its Firestore profile.
    dispatch({ type: actions.VERIFY_START });
    try {
      await sendVerificationEmail(createdUser, '/login?verified=1');
      dispatch({ type: actions.VERIFY_SUCCESS });
    } catch (emailError) {
      console.error('[auth] Verification email could not be sent.', emailError);
      dispatch({
        type: actions.VERIFY_FAIL,
        payload: getFirebaseErrorMessage(emailError),
      });
    }

    dispatch({ type: actions.AUTH_SUCCESS });
    return true;
  } catch (error) {
    console.error('[auth] Account creation failed.', error);

    // Roll back only when Firebase Auth creation, profile initialization or
    // display-name initialization failed. Email errors are handled above.
    if (createdUser) {
      await firestore.collection('users').doc(createdUser.uid).delete().catch(() => undefined);
      await createdUser.delete().catch(() => undefined);
    }

    dispatch({ type: actions.AUTH_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  } finally {
    dispatch({ type: actions.AUTH_END });
  }
};

export const signUp = createAccount('Client');
export const signUpArtisan = createAccount('Artisan');

export const signOut = () => async (dispatch, getState, { getFirebase }) => {
  try {
    await getFirebase().auth().signOut();
    return true;
  } catch (error) {
    dispatch({ type: actions.AUTH_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};

export const signIn = (data) => async (dispatch, getState, { getFirebase }) => {
  dispatch({ type: actions.AUTH_START });
  try {
    await getFirebase()
      .auth()
      .signInWithEmailAndPassword(normalizeText(data.email), data.password);
    dispatch({ type: actions.AUTH_SUCCESS });
    return true;
  } catch (error) {
    dispatch({ type: actions.AUTH_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  } finally {
    dispatch({ type: actions.AUTH_END });
  }
};

export const cleanMessage = () => ({ type: actions.CLEAN_UP });

export const verifyEmail = () => async (dispatch, getState, { getFirebase }) => {
  dispatch({ type: actions.VERIFY_START });
  try {
    const user = getFirebase().auth().currentUser;
    if (!user) throw new Error('Votre session a expiré. Reconnectez-vous.');
    if (!user.emailVerified) await sendVerificationEmail(user, '/login?verified=1');
    dispatch({ type: actions.VERIFY_SUCCESS });
    return true;
  } catch (error) {
    console.error('[auth] Verification email resend failed.', error);
    dispatch({ type: actions.VERIFY_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};

export const recoverPassword = (data) => async (dispatch, getState, { getFirebase }) => {
  dispatch({ type: actions.RECOVERY_START });
  try {
    await sendPasswordReset(normalizeText(data.email), '/login?passwordReset=1');
    dispatch({ type: actions.RECOVERY_SUCCESS });
    return true;
  } catch (error) {
    dispatch({ type: actions.RECOVERY_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};

export const editProfile = (data) => async (
  dispatch,
  getState,
  { getFirebase, getFirestore },
) => {
  const firebase = getFirebase();
  const firestore = getFirestore();
  const user = firebase.auth().currentUser;
  const { uid: userId, email: currentEmail } = getState().firebase.auth;

  dispatch({ type: actions.PROFILE_EDIT_START });
  try {
    if (!user || !userId) throw new Error('Votre session a expiré. Reconnectez-vous.');
    const email = normalizeText(data.email || '').toLowerCase();
    if (!email) throw new Error('L’adresse e-mail est requise.');
    if (email !== currentEmail) await user.updateEmail(email);

    const homeLocation = makeGeoLocation({
      ...(data.addressLocation || {}),
      formattedAddress: data.address || data.addressLocation?.formattedAddress || '',
    });

    await firestore.collection('users').doc(userId).set(
      {
        firstName: normalizeText(data.firstName),
        lastName: normalizeText(data.lastName),
        email,
        phone_number: String(data.phoneNumber || '').trim(),
        user_type: 'Client',
        profile_image: data.profileImage || '',
        address: normalizeText(data.address || ''),
        home_location: homeLocation ? {
          latitude: homeLocation.latitude,
          longitude: homeLocation.longitude,
          geohash: homeLocation.geohash,
          formatted_address: normalizeText(data.address || homeLocation.formattedAddress || ''),
          city: homeLocation.city || '',
          postal_code: homeLocation.postalCode || '',
          updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        } : firebase.firestore.FieldValue.delete(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true },
    );

    dispatch({ type: actions.PROFILE_EDIT_SUCCESS });
    return true;
  } catch (error) {
    dispatch({ type: actions.PROFILE_EDIT_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};

export const editAccountArtisan = (data) => async (
  dispatch,
  getState,
  { getFirebase, getFirestore },
) => {
  const firebase = getFirebase();
  const firestore = getFirestore();
  const user = firebase.auth().currentUser;
  const { uid: userId, email: currentEmail } = getState().firebase.auth;

  dispatch({ type: actions.PROFILE_EDIT_START });
  try {
    if (!user || !userId) throw new Error('Votre session a expiré. Reconnectez-vous.');
    const email = normalizeText(data.email || '').toLowerCase();
    if (!email) throw new Error('L’adresse e-mail est requise.');
    if (email !== currentEmail) await user.updateEmail(email);

    const serviceLocation = makeGeoLocation({
      ...(data.serviceLocation || {}),
      formattedAddress: data.serviceAddress || data.serviceLocation?.formattedAddress || '',
    });

    const primaryRegion = normalizeRegionCode(data.region);
    const serviceRegions = data.acceptsOutsideRegions
      ? normalizeRegionList([primaryRegion, ...(data.serviceRegions || [])])
      : [primaryRegion];

    await firestore.collection('users').doc(userId).set(
      {
        user_id: userId,
        firstName: normalizeText(data.firstName),
        lastName: normalizeText(data.lastName),
        email,
        speciality: data.specialityType,
        user_type: 'Artisan',
        phone_number: String(data.contactNumber || '').trim(),
        address: normalizeText(data.address || ''),
        profile_image: data.profileImage || '',
        artisan_region: primaryRegion,
        service_regions: serviceRegions,
        accepts_outside_regions: Boolean(data.acceptsOutsideRegions),
        nationwide_service: Boolean(data.nationwideService),
        business_intro: normalizeText(data.businessIntroduction || ''),
        certifications: String(data.certifications || '')
          .split(/\r?\n|,/)
          .map((item) => normalizeText(item))
          .filter(Boolean)
          .slice(0, 12),
        service_area: serviceLocation ? {
          base_address: normalizeText(data.serviceAddress || serviceLocation.formattedAddress || ''),
          center: {
            latitude: serviceLocation.latitude,
            longitude: serviceLocation.longitude,
            geohash: serviceLocation.geohash,
          },
          city: serviceLocation.city || '',
          postal_code: serviceLocation.postalCode || '',
          radius_km: Math.min(150, Math.max(5, Number(data.serviceRadius) || 30)),
          updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        } : firebase.firestore.FieldValue.delete(),
        geo_notifications: {
          enabled: Boolean(serviceLocation && data.geoNotifications),
          radius_km: Math.min(150, Math.max(5, Number(data.serviceRadius) || 30)),
          location_ready: Boolean(serviceLocation),
        },
        availability_status: data.availabilityStatus || 'available',
        document_template: {
          business_name: normalizeText(data.businessName || ''),
          legal_form: normalizeText(data.legalForm || ''),
          siret: String(data.siret || '').replace(/\s+/g, ''),
          vat_number: normalizeText(data.vatNumber || ''),
          professional_insurance: normalizeText(data.professionalInsurance || ''),
          website: normalizeText(data.companyWebsite || ''),
          logo_url: data.companyLogo || '',
          quote_validity_days: Math.min(365, Math.max(1, Number(data.quoteValidityDays) || 30)),
          default_tax_mode: data.defaultTaxMode || '',
          payment_terms: normalizeText(data.paymentTerms || ''),
          footer_note: normalizeText(data.documentFooter || ''),
          version: '2026-07-document-template-v1',
          updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        },
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true },
    );

    dispatch({ type: actions.PROFILE_EDIT_SUCCESS });
    return true;
  } catch (error) {
    dispatch({ type: actions.PROFILE_EDIT_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};

export const updateArtisanPreferences = (updates) => async (
  dispatch,
  getState,
  { getFirebase, getFirestore },
) => {
  const firebase = getFirebase();
  const firestore = getFirestore();
  const userId = getState().firebase.auth.uid;
  const allowed = {};

  if (typeof updates.speciality === 'string' && updates.speciality.trim()) {
    allowed.speciality = updates.speciality.trim();
  }
  if (typeof updates.artisan_region === 'string' && updates.artisan_region.trim()) {
    allowed.artisan_region = normalizeRegionCode(updates.artisan_region);
  }
  if (Array.isArray(updates.service_regions)) {
    allowed.service_regions = normalizeRegionList(updates.service_regions);
  }
  if (typeof updates.accepts_outside_regions === 'boolean') {
    allowed.accepts_outside_regions = updates.accepts_outside_regions;
  }
  if (typeof updates.nationwide_service === 'boolean') {
    allowed.nationwide_service = updates.nationwide_service;
  }

  if (!userId || Object.keys(allowed).length === 0) return false;

  dispatch({ type: actions.PROFILE_EDIT_START });
  try {
    await firestore.collection('users').doc(userId).set(
      { ...allowed, updated_at: firebase.firestore.FieldValue.serverTimestamp() },
      { merge: true },
    );
    dispatch({ type: actions.PROFILE_EDIT_SUCCESS });
    return true;
  } catch (error) {
    dispatch({ type: actions.PROFILE_EDIT_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};

export const deleteUser = (password = '') => async (
  dispatch,
  getState,
  { getFirebase },
) => {
  const firebase = getFirebase();
  const user = firebase.auth().currentUser;
  const userId = getState().firebase.auth.uid;

  dispatch({ type: actions.DELETE_START });
  try {
    if (!user || !userId) throw new Error('Votre session a expiré. Reconnectez-vous.');

    const providers = (user.providerData || []).map((entry) => entry?.providerId).filter(Boolean);
    const usesPassword = providers.includes('password');
    if (usesPassword) {
      if (!String(password || '').trim()) {
        const error = new Error('Saisissez votre mot de passe pour confirmer la suppression.');
        error.code = 'auth/password-required';
        throw error;
      }
      const credential = firebase.auth.EmailAuthProvider.credential(user.email, String(password));
      await user.reauthenticateWithCredential(credential);
    } else if (providers.includes('google.com')) {
      const provider = new firebase.auth.GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await user.reauthenticateWithPopup(provider);
    } else {
      throw new Error('Reconnectez-vous avec votre méthode habituelle avant de supprimer le compte.');
    }

    // The backend checks active projects, anonymises retained legal records,
    // recursively removes profile subcollections and finally deletes Auth.
    await deleteMyAccount();
    await firebase.auth().signOut().catch(() => undefined);
    dispatch({ type: actions.DELETE_SUCCESS });
    return true;
  } catch (error) {
    dispatch({ type: actions.DELETE_FAIL, payload: getFirebaseErrorMessage(error) });
    return false;
  }
};